// ============================================
// ITEM TYPES
// ============================================

export type ItemType = 'link' | 'podcast' | 'note' | 'idea' | 'file' | 'task';

export type Category = 'all' | 'inbox' | ItemType | 'favorites';

// ============================================
// BASE ITEM
// ============================================

export interface BaseItem {
  id: string;
  type: ItemType;
  title: string;
  description: string;
  tags: string[];
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
  aiChatHistory?: AIMessage[]; // Per-item AI chat history (persisted)
}

// ============================================
// SPECIFIC ITEM TYPES
// ============================================

export interface LinkItem extends BaseItem {
  type: 'link';
  url: string;
  thumbnail: string | null;
  favicon: string | null;
  siteName: string | null;
  personalNotes: string;
}

export interface PodcastItem extends BaseItem {
  type: 'podcast';
  url: string;
  thumbnail: string | null;
  podcastName: string | null;
  personalNotes: string;
}

export interface NoteItem extends BaseItem {
  type: 'note';
  content: string; // HTML from Tiptap
  plainTextPreview: string; // First 200 chars, stripped of HTML
}

export interface IdeaItem extends BaseItem {
  type: 'idea';
  content: string;
  plainTextPreview: string;
}

export interface FileItem extends BaseItem {
  type: 'file';
  originalFileName: string;
  mimeType: string;
  fileSize: number;
  googleDriveFileId: string;
  webViewLink: string;
  personalNotes: string;
}

export interface TaskItem extends BaseItem {
  type: 'task';
  isCompleted: boolean;
}

// Union type for all items
export type Item = LinkItem | PodcastItem | NoteItem | IdeaItem | FileItem | TaskItem;

// ============================================
// CONFIG
// ============================================

export interface TagDefinition {
  name: string;
  color: string;
}

export interface OnboardingState {
  completedWelcome: boolean;
  dismissedChecklist: boolean;
  completedSteps: string[];
}

export interface AppConfig {
  version: string;
  tags: TagDefinition[];
  lastSyncedAt: string;
  onboarding?: OnboardingState;
}

// ============================================
// AI MESSAGES
// ============================================

/**
 * AI chat message - used for both:
 * - Session chat in AIPanel (not persisted)
 * - Per-item chat history (persisted with item in Drive)
 */
export interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

// ============================================
// UI STATE
// ============================================

export type ModalMode = 'add' | 'edit' | 'view' | 'delete' | 'settings';

export interface ModalState {
  isOpen: boolean;
  mode: ModalMode;
  item: Item | null;
  itemType?: ItemType | undefined;
  prefillTags?: string[] | undefined;
  prefillUrl?: string | undefined;
  prefillTitle?: string | undefined;
  prefillContent?: string | undefined;
  autoStartVoice?: boolean | undefined;
}

export type ViewMode = 'grid' | 'list';

// ============================================
// AUTH
// ============================================

export interface User {
  email: string;
  name: string;
  picture: string;
}

// ============================================
// DATA STATE
// ============================================

export interface ItemsState {
  links: LinkItem[];
  podcasts: PodcastItem[];
  notes: NoteItem[];
  ideas: IdeaItem[];
  files: FileItem[];
  tasks: TaskItem[];
}

// ============================================
// TYPE GUARDS
// ============================================

export function isLinkItem(item: Item): item is LinkItem {
  return item.type === 'link';
}

export function isPodcastItem(item: Item): item is PodcastItem {
  return item.type === 'podcast';
}

export function isNoteItem(item: Item): item is NoteItem {
  return item.type === 'note';
}

export function isIdeaItem(item: Item): item is IdeaItem {
  return item.type === 'idea';
}

export function isFileItem(item: Item): item is FileItem {
  return item.type === 'file';
}

export function isTaskItem(item: Item): item is TaskItem {
  return item.type === 'task';
}

// ============================================
// KNOWLEDGE BASE (WIKI) TYPES
// ============================================

export type WikiPageKind = 'entity' | 'concept' | 'synthesis';

export interface WikiPage {
  id: string;
  slug: string;
  title: string;
  kind: WikiPageKind;
  content: string;
  backlinks: string[];
  relatedPages: string[];
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt: string;
}

export interface WikiIndex {
  pages: WikiIndexEntry[];
  lastSyncedAt: string;
  version: number;
}

export interface WikiIndexEntry {
  slug: string;
  title: string;
  kind: WikiPageKind;
  itemCount: number;
  updatedAt: string;
}

export interface WikiSyncResult {
  create: WikiPage[];
  update: Array<Partial<WikiPage> & { slug: string }>;
  delete: string[];
}

// ============================================
// GOOGLE DRIVE API TYPES
// ============================================

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  webViewLink?: string;
}

export interface DriveListResponse {
  files: DriveFile[];
  nextPageToken?: string;
}

// ============================================
// METADATA TYPES
// ============================================

export interface LinkMetadata {
  title: string | null;
  description: string | null;
  image: string | null;
  favicon: string | null;
  siteName: string | null;
}

// ============================================
// OPENROUTER API TYPES
// ============================================

export interface OpenRouterMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface OpenRouterRequest {
  model: string;
  messages: OpenRouterMessage[];
  temperature?: number;
  max_tokens?: number;
}

export interface OpenRouterResponse {
  id: string;
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// ============================================
// EVENT TYPES
// ============================================

export interface DriveWriteErrorDetail {
  filename: string;
  error: Error;
}

declare global {
  interface WindowEventMap {
    'drive-write-error': CustomEvent<DriveWriteErrorDetail>;
  }
}
